<script lang="ts">
export default {
  name: 'AppModal',
};
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
    title?: string;
    cancelButtonLabel?: string;
    actionButtonLabel?: string;
    handleCancel?: () => void;
    handleAction?: () => void;
    loading?: boolean;
    actionDisabled?: boolean;
    modalClass?: string;
    dialogClass?: string;
    disableTeleport?: boolean;
    showOverflow?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
    layout?: 'single' | 'two-column';
  }>(),
  {
    disableTeleport: false,
    showOverflow: false,
    size: 'md',
    layout: 'single',
  },
);

const dialogRef = ref<HTMLDialogElement | null>(null);

const cancelButtonLabel = computed(() => props.cancelButtonLabel ?? 'Cancel');
const actionButtonLabel = computed(() => props.actionButtonLabel ?? 'Save');
const handleCancel = computed(
  () =>
    props.handleCancel ??
    (() => {
      console.log('cancel');
    }),
);
const handleAction = computed(
  () =>
    props.handleAction ??
    (() => {
      console.log('action');
    }),
);

// Modal sizing computed properties
const modalSizeClasses = computed(() => {
  const sizeMap = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    xxl: 'max-w-5xl',
  };
  return sizeMap[props.size];
});

const contentLayoutClasses = computed(() => {
  if (props.layout === 'two-column') {
    return 'grid grid-cols-1 md:grid-cols-2 gap-6';
  }
  return '';
});

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) {
    e.preventDefault();
    handleCancel.value();
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));

function open() {
  dialogRef.value?.showModal();
}

function close() {
  dialogRef.value?.close();
}

defineExpose({
  open,
  close,
  dialogRef,
});
</script>

<template>
  <Teleport
    to="body"
    :disabled="disableTeleport">
    <dialog
      class="modal z-1100 items-center"
      :class="[{ 'modal-open': show }, dialogClass ?? '']"
      ref="dialogRef">
      <div
        class="form-control modal-box flex max-h-[90vh] flex-col gap-4 overflow-hidden rounded-xl"
        :class="[modalSizeClasses, modalClass ?? '', { 'overflow-visible': showOverflow }]">
        <div class="flex shrink-0 flex-col">
          <slot name="title">
            <h1 v-if="title">{{ title }}</h1>
          </slot>
        </div>
        <div
          class="min-h-0 w-full flex-1"
          :class="[{ 'overflow-y-auto': !showOverflow }, contentLayoutClasses]">
          <!-- Single column layout -->
          <template v-if="layout === 'single'">
            <slot name="content"></slot>
          </template>

          <!-- Two column layout -->
          <template v-else-if="layout === 'two-column'">
            <div class="space-y-4">
              <slot name="left-column"></slot>
            </div>
            <div class="space-y-4">
              <slot name="right-column"></slot>
            </div>
          </template>
        </div>
        <div class="modal-action shrink-0 items-center">
          <slot name="extra-actions"> </slot>

          <slot name="actions">
            <button
              class="btn btn-sm"
              @click="handleCancel">
              {{ cancelButtonLabel }}
            </button>
            <button
              class="btn text-white btn-sm btn-primary"
              :class="{ 'btn-disabled': actionDisabled }"
              @click="handleAction">
              <span
                v-if="loading"
                class="loading mx-2 loading-sm loading-dots"></span>
              <span v-else>{{ actionButtonLabel }}</span>
            </button>
          </slot>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>
